<template lang="pug">
.competition-schedule.main-container
  Tabs(:title="competition.compYear + ' Schedule'" :topHeading="true" :tabs="tabs" v-if="tabs.length > 2" v-model="activeTab")
  suspense(v-for="day in days" :key="day.day")
    template(#default)
      EventSchedule(:event-day="day.day" :events="day.events" :expanded="day.expanded" :time-zone-label="day.timeZoneLabel")
    template(#fallback)
      EventScheduleLoading(:event-day="day.day" :events="day.events" :expanded="day.expanded" :time-zone-label="day.timeZoneLabel")
</template>
<script setup lang="ts">
import { ref, computed } from 'vue'
import EventScheduleLoading from './EventScheduleLoading.vue'

interface Competition {
  baseUrl: string;
  hidePastSchedule?: boolean;
  [key: string]: any;
}

interface Props {
  competition: Competition
  events?: any[]
  timeZone?: string
  excludeFromEventSchedule?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  events                   : () => ([]),
  timeZone                 : '',
  excludeFromEventSchedule : () => ([]),
})

// State
const activeTab = ref('Event Schedule')

// Computed: Tabs
const tabs = computed(() => {
  const retval = ['Event Schedule']
  const divisions = ['Individual', 'Men', 'Women', 'Strongman', 'Strongwoman', 'CrossFit']

  props.events.forEach(e => {
    if (divisions.includes(e.division) && !retval.includes(`${e.division} Events`)) {
      retval.push(e.division)
    }
  })

  return [...new Set(retval)]
})

// Invitational times are entered as UK wall-clock time and rendered in Etc/Greenwich so they display
// unshifted. Label them with the real UK zone so days either side of the BST change read correctly.
const labelTimeZones: Record<string, string> = {
  'Etc/Greenwich': 'Europe/London',
}

// Short zone name in effect on the day an event falls on, e.g. "GMT+1", "GMT", "EDT"
const getDayTimeZoneLabel = (time: string) => {
  if (!props.timeZone || !time) return ''

  try {
    const dateParts = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone: props.timeZone })
      .formatToParts(new Date(time))
    const datePart = (type: string) => Number(dateParts.find(p => p.type === type)?.value)
    // Midday keeps clear of the overnight hour when clocks change
    const midday = new Date(Date.UTC(datePart('year'), datePart('month') - 1, datePart('day'), 12))

    const label = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: labelTimeZones[props.timeZone] || props.timeZone })
      .formatToParts(midday)
      .find(p => p.type === 'timeZoneName')?.value || ''

    // Some engines render a zero offset as "GMT+0"
    return label === 'GMT+0' ? 'GMT' : label
  } catch {
    return ''
  }
}

const days = computed(() => {
  const retval: any[] = []
  const timeZoneString = props.timeZone

  const filteredEvents = props.events.filter(e =>
    activeTab.value === 'Event Schedule'
      ? !props.excludeFromEventSchedule.includes(e.division)
      : e.division === activeTab.value.replace(' Events', ''))

  filteredEvents
    .forEach(e => {

      const dateString = new Date(e.time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: timeZoneString })
      let day = retval.find(d => d.day === dateString)
      if (!day) {
        day = { day: dateString, events: [], expanded: true, timeZoneLabel: getDayTimeZoneLabel(e.time) }
        retval.push(day)
      }
      let startTime = ''

      if (e.time) {
        startTime = e.time.endsWith('T00:00:00.000+00:00')
          ? 'TBA'
          : new Date(e.time).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: timeZoneString })
      }
      let endTime = ''
      if (e.endTime) {
        endTime = new Date(e.endTime).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: timeZoneString })
      }
      day.events.push({
        title             : e.title,
        originalStartTime : e.time,
        startTime,
        endTime,
        location          : e.location,
        subtitle          : e.subtitle,
        description       : e.description,
        logo              : e.sponsorLogo,
        replayLink        : e.replayId ? `${props.competition.baseUrl}/${e.division}/${e.eventId}`.toLowerCase() : null,
        link              : e.link,
        linkText          : e.linkText,
        linkNewTab        : e.linkNewTab,
        linkDisabled      : e.linkDisabled,
        timestamp         : e.time,
      })
      day.events = day.events.sort((e1, e2) => {
        const e1Date = Date.parse(e1.originalStartTime)
        const e2Date = Date.parse(e2.originalStartTime)

        return e1Date - e2Date
      })
    })
  // Allow hiding past schedule rows
  if (props.competition.hidePastSchedule) {
    retval.forEach(day => day.expanded = day.events.some(e => new Date(e.timestamp) > new Date()))
  }

  return retval
})
</script>
